import React, { useEffect, useMemo, useState } from 'react';
import styles from './OptionsEditorPopup.module.css';

type OptionsEditorPopupProps = {
  isVisible: boolean;
  title: string;
  options: string[];
  onClose: () => void;
  onSave: (options: string[]) => void;
  requireAtLeastOneOption?: boolean;
};

const OptionsEditorPopup = ({
  isVisible,
  title,
  options,
  onClose,
  onSave,
  requireAtLeastOneOption = false,
}: OptionsEditorPopupProps) => {
  const [tempValue, setTempValue] = useState('');
  const [tempOptions, setTempOptions] = useState<string[]>(options);

  useEffect(() => {
    if (isVisible) {
      setTempOptions(options);
      setTempValue('');
    }
  }, [isVisible, options]);

  const canSave = useMemo(() => {
    return requireAtLeastOneOption ? tempOptions.length > 0 : true;
  }, [requireAtLeastOneOption, tempOptions.length]);

  const handleAddOption = () => {
    const value = tempValue.trim();
    if (!value || tempOptions.includes(value)) {
      setTempValue('');
      return;
    }

    setTempOptions((prev) => [...prev, value]);
    setTempValue('');
  };

  const handleRemoveOption = (index: number) => {
    setTempOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setTempOptions(options);
    setTempValue('');
    onClose();
  };

  const handleSave = () => {
    if (!canSave) return;
    onSave(tempOptions);
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className={styles.optionsModal} onClick={handleCancel}>
      <div className={styles.optionsModalContent} onClick={(e) => e.stopPropagation()}>
        <h4>Edit Options for {title}</h4>

        <div className={styles.optionInputContainer}>
          <input
            type="text"
            placeholder="Enter option..."
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddOption()}
            className={styles.optionInput}
          />
          <button type="button" onClick={handleAddOption} className={styles.addOptionButton}>
            Add Option
          </button>
        </div>

        {tempOptions.length > 0 && (
          <div className={styles.optionsList}>
            {tempOptions.map((option, index) => (
              <div key={`${option}-${index}`} className={styles.optionItem}>
                <span>{option}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveOption(index)}
                  className={styles.removeOptionButton}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div className={styles.modalActions}>
          <button type="button" onClick={handleCancel} className={styles.cancelButton}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className={styles.saveButton}
            disabled={!canSave}
          >
            Save Options
          </button>
        </div>
      </div>
    </div>
  );
};

export default OptionsEditorPopup;
